import { BALANCE, DIFFICULTY, PRACTICE, REFERENCE_SOLVE_MS } from './balance.ts';
import { scaleTenths } from './game/core.ts';
import { getMap } from './maps/graph.ts';
import { mapPublic } from './view/playerView.ts';
import { validatePhrase } from './phrases.ts';
import { PUZZLES, createPuzzle } from './puzzles/registry.ts';
import type { ApplyOk, PuzzleEvent, PuzzlePlayer } from './puzzles/types.ts';
import { Rng } from './rng.ts';
import type {
  ActionError,
  GameEvent,
  Language,
  PhraseRef,
  PlayerId,
  PuzzleAct,
  PuzzleInstance,
  PuzzleType,
  Role,
} from './types.ts';
import type { PlayerView, ViewEvent } from './view/playerView.ts';

/**
 * Training (GDD §11): one puzzle at a time, played on its own against the clock, with every
 * piece of information on the table. The puzzle is the very module the heist uses; this file
 * only supplies what a heist would: five seats (one per role, the player in the solver's), a
 * clock, an alarm and a verdict. Pure and deterministic like the rest of the engine: the same
 * options and the same actions always play out the same way.
 */

export const PRACTICE_ROLES: readonly Role[] = [
  'mastermind',
  'locksmith',
  'hacker',
  'lookout',
  'driver',
];

export interface PracticeOptions {
  type: PuzzleType;
  /** 1–5, the puzzle's own difficulty level. */
  level: number;
  seed: string;
  language: Language;
  /** Display names of the five seats; the first is the player's. */
  names: readonly string[];
}

export type PracticeStatus = 'running' | 'won' | 'lost';
export type PracticeLoss = 'time' | 'alarm' | 'vault';

export interface PracticeSeat extends PuzzlePlayer {
  name: string;
}

export interface PracticeState {
  options: PracticeOptions;
  puzzle: PuzzleInstance;
  /** The player is `seats[0]`. */
  seats: PracticeSeat[];
  clockMs: number;
  /** Alarm in tenths of a percent, like the heist's. */
  alarm: number;
  errors: number;
  parMs: number;
  limitMs: number;
  status: PracticeStatus;
  lostBy: PracticeLoss | null;
  /** Cameras slowly raise the alarm while they watch (GDD §6.3). */
  dripAccMs: number;
  log: GameEvent[];
  nextEventId: number;
}

/** The role that works the puzzle with their hands (the player takes this seat). */
export function practiceRole(type: PuzzleType): Role {
  switch (type) {
    case 'lock':
    case 'safe':
    case 'laser':
      return 'locksmith';
    case 'panel':
    case 'cameras':
    case 'password':
      return 'hacker';
    case 'corridor':
      return 'lookout';
    case 'escape':
      return 'driver';
    case 'vault':
      return 'mastermind';
  }
}

export function practiceParMs(type: PuzzleType, level: number): number {
  const factor = 1000 + PRACTICE.parPerLevelPermille * (level - 1);
  return Math.round((REFERENCE_SOLVE_MS[type] * factor) / 1000);
}

/** Roles in seat order: the player's first, then the others in the usual order. */
function seatRoles(type: PuzzleType): Role[] {
  const mine = practiceRole(type);
  return [mine, ...PRACTICE_ROLES.filter((r) => r !== mine)];
}

export function createPractice(options: PracticeOptions): PracticeState {
  const level = Math.min(5, Math.max(1, Math.round(options.level)));
  const roles = seatRoles(options.type);
  // A corridor is crossed by one token here: the other seats do not walk it. Their clues
  // still exist, so the player sees them next to the puzzle.
  const alone = options.type === 'corridor';
  const seats: PracticeSeat[] = roles.map((role, i) => ({
    id: `p${i + 1}`,
    role,
    seat: i,
    expelled: alone && i > 0,
    coins: role === 'lookout' ? BALANCE.coins : 0,
    petards: role === 'lookout' ? BALANCE.petards : 0,
    name: options.names[i] ?? `p${i + 1}`,
  }));
  const map = getMap('bank');
  const room = options.type === 'escape' || options.type === 'vault' ? map.vault : roomOf(map);
  const rng = Rng.create(`${options.seed}/practice/${options.type}/${level}`);
  const puzzle = createPuzzle('pz1', options.type, {
    rng,
    level,
    roles,
    players: seats,
    language: options.language,
    room,
    map,
    difficulty: 'normal',
    alarmTenths: 0,
    guardDelta: 0,
    dimVision: false,
  });
  const parMs = practiceParMs(options.type, level);
  return {
    options: { ...options, level },
    puzzle,
    seats,
    clockMs: 0,
    alarm: 0,
    errors: 0,
    parMs,
    limitMs: parMs * PRACTICE.limitFactor,
    status: 'running',
    lostBy: null,
    dripAccMs: 0,
    log: [],
    nextEventId: 1,
  };
}

function roomOf(map: ReturnType<typeof getMap>): string {
  const first = map.rooms[0];
  if (!first) throw new Error('map without rooms');
  return first.id;
}

function emit(
  s: PracticeState,
  type: string,
  data: Record<string, unknown>,
  audience: GameEvent['audience'] = 'all',
): void {
  s.log.push({ id: s.nextEventId++, type, atMs: s.clockMs, audience, data });
}

function seatOf(s: PracticeState, role: Role): PracticeSeat | undefined {
  return s.seats.find((p) => p.role === role);
}

function fail(code: ActionError['code'], message: string): ActionError {
  return { code, message };
}

function addAlarm(s: PracticeState, tenths: number, scaled: boolean): void {
  if (tenths <= 0 || s.status !== 'running') return;
  const amount = scaled ? scaleTenths(tenths, DIFFICULTY.normal.penaltyPermille) : tenths;
  const before = s.alarm;
  s.alarm = Math.min(BALANCE.alarmMax, s.alarm + amount);
  emit(s, 'alarm', { alarm: s.alarm, delta: s.alarm - before, source: scaled ? 'error' : 'other' });
  if (s.alarm >= BALANCE.alarmMax) lose(s, 'alarm');
}

function lose(s: PracticeState, by: PracticeLoss): void {
  if (s.status !== 'running') return;
  s.status = 'lost';
  s.lostBy = by;
  emit(s, 'practice.lost', { reason: by });
}

interface Outcome {
  errorTenths: number;
  fixedTenths: number;
  solved: boolean;
  events: PuzzleEvent[];
}

function settle(s: PracticeState, r: Outcome): void {
  const p = s.puzzle;
  for (const ev of r.events) emit(s, ev.type, { ...ev.data, puzzleId: p.id }, ev.audience ?? 'all');
  if (r.errorTenths > 0) {
    s.errors += 1;
    addAlarm(s, r.errorTenths, true);
  }
  addAlarm(s, r.fixedTenths, false);
  if (s.status !== 'running') return;
  if (p.type === 'vault' && (p.state as { failed: boolean }).failed) {
    lose(s, 'vault');
    return;
  }
  if (r.solved && !p.solved) {
    p.solved = true;
    p.solvedAtMs = s.clockMs;
    s.status = 'won';
    emit(s, 'puzzle.solved', { puzzleId: p.id, type: p.type, room: p.room });
  }
}

function applyAs(s: PracticeState, seat: PracticeSeat, act: PuzzleAct): ApplyOk | ActionError {
  const r = PUZZLES[s.puzzle.type].apply(s.puzzle, act, {
    actor: seat.id,
    actorRole: seat.role,
    players: s.seats,
    execMs: s.clockMs,
    alarmTenths: s.alarm,
    looped: false,
    tunnel: false,
    dimVision: false,
  });
  return r.ok ? r : r.error;
}

/**
 * The player acts on the puzzle from the seat of `role`. Changes `s` in place; returns the
 * reason when the move is refused.
 */
export function practiceAct(s: PracticeState, role: Role, act: PuzzleAct): ActionError | null {
  if (s.status !== 'running') return fail('gameOver', 'the training is over');
  const seat = seatOf(s, role);
  if (!seat || seat.expelled) return fail('notAllowed', 'no such seat');
  const r = applyAs(s, seat, act);
  if ('code' in r) return r;
  if (r.consumes === 'coin') seat.coins -= 1;
  else if (r.consumes === 'petard') seat.petards -= 1;
  settle(s, r);
  // The vault wants everybody's confirmation: the teammates confirm along with the player,
  // and whoever confirms last is the one the code is checked on.
  if (
    s.puzzle.type === 'vault' &&
    (act.kind === 'vault.confirm' || act.kind === 'vault.unconfirm')
  ) {
    for (const other of s.seats) {
      if (other === seat || s.status !== 'running') continue;
      const o = applyAs(s, other, act);
      if (!('code' in o)) settle(s, o);
    }
  }
  return null;
}

/** Anybody at the table says a quick phrase (the teammates' clues are said for them). */
export function practiceSay(s: PracticeState, role: Role, phrase: PhraseRef): ActionError | null {
  const seat = seatOf(s, role);
  if (!seat) return fail('notAllowed', 'no such seat');
  const problem = validatePhrase(phrase);
  if (problem) return fail('invalid', problem);
  emit(s, 'chat', { by: seat.id, phrase });
  return null;
}

/** Lets `dtMs` of game time pass. Changes `s` in place. */
export function practiceTick(s: PracticeState, dtMs: number): void {
  if (s.status !== 'running' || dtMs <= 0) return;
  s.clockMs += dtMs;
  const p = s.puzzle;
  const module = PUZZLES[p.type];
  if (p.type === 'cameras') {
    s.dripAccMs += dtMs;
    while (s.dripAccMs >= BALANCE.cameraDripEveryMs && s.status === 'running') {
      s.dripAccMs -= BALANCE.cameraDripEveryMs;
      addAlarm(s, BALANCE.fixed.cameraDrip, false);
    }
  }
  if (s.status === 'running' && module.tick) {
    const r = module.tick(p, dtMs, {
      execMs: s.clockMs,
      alarmTenths: s.alarm,
      players: s.seats,
      roles: s.seats.map((x) => x.role),
      tunnel: false,
      dimVision: false,
    });
    settle(s, { ...r, fixedTenths: 0 });
  }
  if (s.status === 'running' && s.clockMs >= s.limitMs) lose(s, 'time');
}

/** 0 when the training was lost or is still running; 1–3 when solved (GDD §11). */
export function practiceStars(s: PracticeState): 0 | 1 | 2 | 3 {
  if (s.status !== 'won') return 0;
  const { threeStars, twoStars } = PRACTICE;
  if (s.clockMs <= s.parMs * threeStars.timeFactor && s.alarm <= threeStars.maxAlarmTenths)
    return 3;
  if (s.clockMs <= s.parMs * twoStars.timeFactor && s.alarm <= twoStars.maxAlarmTenths) return 2;
  return 1;
}

function eventsFor(s: PracticeState, id: PlayerId): ViewEvent[] {
  return s.log
    .filter((ev) => ev.audience === 'all' || ev.audience.includes(id))
    .slice(-120)
    .map((ev) => ({ id: ev.id, type: ev.type, atMs: ev.atMs, data: ev.data }));
}

/**
 * What the seat of `role` sees, in the shape of a heist view with a single puzzle, so that the
 * screens of the heist play the training unchanged. Asking for every role in turn is how
 * "all the information" is put on the table.
 */
export function practiceView(s: PracticeState, role: Role): PlayerView {
  const me = seatOf(s, role);
  if (!me) throw new Error(`no seat for ${role}`);
  const p = s.puzzle;
  const over = s.status !== 'running';
  const map = mapPublic(getMap('bank'));
  return {
    you: {
      id: me.id,
      role,
      isInfiltrator: false,
      sabotagesLeft: null,
      powers: { planB: 0, loops: 0, coins: me.coins, petards: me.petards },
    },
    game: {
      phase: 'execution',
      clockMs: s.clockMs,
      phaseStartedMs: 0,
      mapId: 'bank',
      difficulty: 'normal',
      language: s.options.language,
      playerCount: s.seats.length,
      baseLoot: 0,
      timerMs: s.limitMs,
    },
    players: s.seats.map((x) => ({
      id: x.id,
      name: x.name,
      role: x.role,
      seat: x.seat,
      expelled: x.expelled,
      pin: null,
    })),
    briefing: null,
    planning: null,
    exec: {
      map,
      timerMs: Math.max(0, s.limitMs - s.clockMs),
      ratePermille: 1000,
      elapsedMs: s.clockMs,
      alarm: s.alarm,
      policeAlerted: s.alarm >= BALANCE.policeAlertAt,
      optionalClosed: false,
      looped: false,
      tunnel: false,
      gear: [],
      stops: [],
      stopIndex: 0,
      room: p.room,
      completedOptional: [],
      puzzles: [
        {
          id: p.id,
          type: p.type,
          room: p.room,
          solved: p.solved,
          view: PUZZLES[p.type].view(p, {
            playerId: me.id,
            role,
            roles: s.seats.map((x) => x.role),
            players: s.seats,
            execMs: s.clockMs,
            alarmTenths: s.alarm,
            tunnel: false,
            revealed: over,
          }),
        },
      ],
      planB: null,
      drone: null,
    },
    hideout: null,
    result: null,
    events: eventsFor(s, me.id),
    timeline: [],
  };
}

/** The roles that hold at least one clue of the puzzle, the player's own excluded. */
export function practiceClueHolders(s: PracticeState): Role[] {
  const mine = s.seats[0]?.role;
  const holders = new Set(s.puzzle.clues.map((c) => c.holder));
  return PRACTICE_ROLES.filter((r) => r !== mine && holders.has(r));
}
